import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { initialRefereeDetailsStepFormValues } from 'models/forms/applicationForm';
import ApplicationStore from 'state/ApplicationStore';
import { EDictionaryTypes } from 'models/dictionary';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { useApplicationDetails } from 'pages/MyApplicationsPage/ApplicationDetailsPage/ApplicationDetailsStep/hooks';
import {
  StyledParagraph,
  StyledSectionTitle,
  StyledStepTitle,
} from 'pages/MyApplicationsPage/ApplicationDetailsPage/ApplicationDetailsStep/WizardSteps/styles';

const RefereeDetailsStep = () => {
  const { loadDictionaries, civilianPrefix } = useApplicationDetails();

  const {
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: initialRefereeDetailsStepFormValues,
  });

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.civilianPrefix]);
    })();
  }, []);

  useEffect(() => {
    const formData = ApplicationStore.getStepData('refereeData');
    if (Object.keys(formData).length > 0) {
      reset(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const refereeForm = (number: string) => (
    <Grid container rowGap={1}>
      <StyledSectionTitle variant="h5">{`Referee ${number}`}</StyledSectionTitle>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormSelect
            options={mapDictionaryToOptionProp(civilianPrefix)}
            setValue={setValue}
            name={`referee${number}.refereeTitle`}
            label="Title"
            required
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeSurname`}
            label="Surname"
            required
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeForenames`}
            label="Forenames"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereePreviousNames`}
            label="Previous Names"
            required
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormDatePicker
            name={`referee${number}.refereeBirthDate`}
            openTo="year"
            label="Birth Date*"
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeBirthPlace`}
            label="Birth Place"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <FormInput
            name={`referee${number}.refereeHomeAddress`}
            label="Home Address"
            required
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeHomePostcode`}
            label="Home Postcode"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeHomeMobileNumber`}
            label="Home Mobile Number"
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeHomeEmail`}
            label="Home E-mail"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormInput
            name={`referee${number}.refereeOccupation`}
            label="Occupation"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeWorkMobileNumber`}
            label="Work Mobile Number"
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeWorkEmail`}
            label="Work Email"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormInput
            name={`referee${number}.refereeCapacity`}
            label="In what capacity do you know the referee?"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormInput
            name={`referee${number}.refereeKnown`}
            label="How long has the referee known you?"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormRadioGroup
            name={`referee${number}.refereeGuidanceInformation`}
            label="Have you provided the referee with the guidance information for this application?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ marginTop: '24px' }}>
      <StyledStepTitle variant="h4">Referee Details</StyledStepTitle>
      <StyledParagraph variant="body2">
        Please give details of a suitable person who has agreed to act as a referee for you.
      </StyledParagraph>

      <form>
        {refereeForm('1')}
        {refereeForm('2')}
      </form>
    </Box>
  );
};

export default RefereeDetailsStep;
